/**
 * 回收站 / 软删除恢复 services · AI 智能分类
 *
 * 这一组接口跟 ai-folder-classifier SSE **完全无关** —— 删除/恢复/列表
 * 全走 BFF（WebXCameraAPI）→ photobook RPC 标准链路。LLM 只输出
 * delete_confirm 卡片提示，**永不接触** photoBook 的删/恢复 RPC。
 *
 * 端点（BFF 已加 admin 校验 + audit logger）：
 *   POST /web/folder/delete         body:{folderID, groupID}
 *   POST /web/folder/batch/delete   body:{folderIDs, groupID}
 *   POST /web/folder/restore        body:{folderID, groupID}
 *   POST /web/folder/batch/restore  body:{folderIDs, groupID}
 *   POST /web/folder/deleted/list   body:{groupID, page, pageSize}
 *
 * 所有删除/恢复请求统一带 `X-Op-Source: ai_chat`（AI 抽屉发起）或
 * `recycle_bin`（回收站抽屉发起），落 BFF audit logger 用以区分来源。
 */

declare const __API_SERVER_HOST__: string

export type OpSource = 'ai_chat' | 'recycle_bin' | 'manual'

export interface DeletedFolderItem {
  folderID: number
  folderName: string
  parentID: number
  photoCount: number
  bookCoverImg: string
  deletedAtUnix: number
}

interface BFFEnvelope<T> {
  status: number
  msg?: string
  total?: number
  data?: T
}

async function postJSON<TBody, TData>(
  path: string,
  body: TBody,
  source: OpSource
): Promise<BFFEnvelope<TData>> {
  const res = await fetch(__API_SERVER_HOST__ + path, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Op-Source': source
    },
    body: JSON.stringify(body)
  })
  // BFF 返回结构：{code:200, data:{status, msg, ...}}（fetchHelper 同款）
  // 但这里我们要原始 status 字段，所以直接读 res.data；非 2xx 抛错
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${res.statusText}`)
  }
  const j = await res.json()
  // BFF middleware.Response 的 envelope：{ code: 200, data: {status, msg, ...} }
  if (j && typeof j === 'object' && 'data' in j && j.data) {
    return j.data as BFFEnvelope<TData>
  }
  return j as BFFEnvelope<TData>
}

export async function deleteFolder(
  groupID: string,
  folderID: number,
  source: OpSource = 'ai_chat'
): Promise<BFFEnvelope<unknown>> {
  return postJSON('/web/folder/delete', { groupID, folderID }, source)
}

export async function restoreFolder(
  groupID: string,
  folderID: number,
  source: OpSource = 'recycle_bin'
): Promise<BFFEnvelope<unknown>> {
  return postJSON('/web/folder/restore', { groupID, folderID }, source)
}

export async function batchRestoreFolders(
  groupID: string,
  folderIDs: number[],
  source: OpSource = 'recycle_bin'
): Promise<BFFEnvelope<unknown>> {
  return postJSON('/web/folder/batch/restore', { groupID, folderIDs }, source)
}

export async function fetchDeletedFolderList(
  groupID: string,
  page: number,
  pageSize: number
): Promise<{ total: number; items: DeletedFolderItem[] }> {
  const resp = await postJSON<
    { groupID: string; page: number; pageSize: number },
    DeletedFolderItem[]
  >('/web/folder/deleted/list', { groupID, page, pageSize }, 'recycle_bin')
  const items = Array.isArray(resp.data)
    ? (resp.data as DeletedFolderItem[])
    : []
  return {
    total: typeof resp.total === 'number' ? resp.total : items.length,
    items
  }
}
